// /src/pages/Viewer.jsx
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Grid, List, Book, Trees, Flower, TreeDeciduous, Palmtree, LeafyGreen, GitBranch } from 'lucide-react'
import { useAuth } from '../utils/authContext'
import usePlantStore from '../utils/plantStore'
import PlantCard from '../components/PlantCard'
import Layout from '../components/Layout'

const Viewer = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [viewMode, setViewMode] = useState('grid')
  const { plants, fetchPlants } = usePlantStore()

  // Fetch plants on component mount
  useEffect(() => {
    fetchPlants()
  }, [fetchPlants])



  const categories = [
    { id: 'all', name: 'All Plants', icon: Book, count: plants.length },
    { id: 'trees', name: 'Trees', icon: Trees, count: plants.filter(p => p.category === 'trees').length },
    { id: 'flowers', name: 'Flowers', icon: Flower, count: plants.filter(p => p.category === 'flowers').length },
    { id: 'succulents', name: 'Succulents', icon: TreeDeciduous, count: plants.filter(p => p.category === 'succulents').length },
    { id: 'palms', name: 'Palms', icon: Palmtree, count: plants.filter(p => p.category === 'palms').length },
    { id: 'ferns', name: 'Ferns', icon: LeafyGreen, count: plants.filter(p => p.category === 'ferns').length },
    { id: 'climbers', name: 'Climbers', icon: GitBranch, count: plants.filter(p => p.category === 'climbers').length },
  ]

  const filteredPlants = plants.filter(plant => {
    const matchesSearch = plant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plant.scientificName.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || plant.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  return (
    <Layout title="Plant Database">
      <div className="p-6">

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-forest-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search plants by name or scientific name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-forest-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-primary-100 text-primary-700' : 'bg-white text-forest-600'}`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-primary-100 text-primary-700' : 'bg-white text-forest-600'}`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === category.id
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-white text-forest-600 hover:bg-forest-50'
              }`}
            >
              <category.icon className="w-4 h-4" />
              <span>{category.name}</span>
              <span className="text-xs bg-forest-100 text-forest-600 px-2 py-1 rounded-full">
                {category.count}
              </span>
            </button>
          ))}
        </div>

        {/* Results */}
        <div className="mb-4">
          <p className="text-forest-600">
            Showing {filteredPlants.length} of {plants.length} plants
          </p>
        </div>

        {/* Plants Grid/List */}
        {plants.length === 0 ? (
          <div className="text-center py-12">
            <Book className="w-16 h-16 text-forest-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-forest-900 mb-2">Plant Gallery is Empty</h3>
            <p className="text-forest-600 mb-4">No plants have been added to the database yet.</p>
            <div className="bg-forest-50 border border-forest-200 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-sm text-forest-700">
                <strong>For Admins:</strong> Add plants through the admin panel to populate this gallery.
              </p>
            </div>
          </div>
        ) : filteredPlants.length === 0 ? (
          <div className="text-center py-12">
            <Book className="w-12 h-12 text-forest-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-forest-900 mb-2">No plants found</h3>
            <p className="text-forest-600">Try adjusting your search or filter criteria.</p>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' : 'space-y-4'}>
            {filteredPlants.map((plant) => (
              <PlantCard key={plant.id} plant={plant} viewMode={viewMode} showActions={false} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}

export default Viewer 